/**
 * Cómo se nombra el mando de un hilo, y qué se avisa antes de cambiarlo.
 * Spec 074. La acción que viaja al canal sigue siendo `tomar`.
 */
export type Mando = 'agendify' | 'equipo' | 'cerrada'
export type AccionMando = 'tomar' | 'devolver' | 'cerrar'

export interface MarcaMando {
    nombre: string
    detalle: string
    /** El punto y el nombre. El mismo tono que el botón que deja el hilo aquí. */
    punto: string
    texto: string
    boton: string
}

/**
 * Azul: lo lleva Agendify. Ámbar: contestas tú. Gris: cerrado.
 * Un botón y la marca del estado al que lleva comparten estas clases.
 */
const TONO: Record<Mando, Pick<MarcaMando, 'punto' | 'texto' | 'boton'>> = {
    agendify: {
        punto: 'bg-sky-400',
        texto: 'text-sky-300 light:text-sky-800',
        boton: 'bg-sky-800 text-sky-50 hover:bg-sky-700 light:bg-sky-600 light:text-white light:hover:bg-sky-500',
    },
    equipo: {
        punto: 'bg-amber-400',
        texto: 'text-amber-300 light:text-amber-800',
        boton: 'bg-amber-700 text-amber-50 hover:bg-amber-600 light:bg-amber-500 light:text-neutral-950 light:hover:bg-amber-400',
    },
    cerrada: {
        punto: 'bg-neutral-400',
        texto: 'text-neutral-300 light:text-neutral-700',
        boton: 'bg-neutral-700 text-neutral-100 hover:bg-neutral-600 light:bg-neutral-500 light:text-white light:hover:bg-neutral-600',
    },
}

const DESTINO: Record<AccionMando, Mando> = {
    tomar: 'equipo',
    devolver: 'agendify',
    cerrar: 'cerrada',
}

export const MARCA_MANDO: Record<Mando | 'desconocido', MarcaMando> = {
    agendify: {
        nombre: 'Agendify atiende',
        detalle: 'No puedes escribir. Para hablar con el cliente, contesta tú.',
        ...TONO.agendify,
    },
    equipo: {
        nombre: 'Contestando tú',
        detalle: 'Agendify calla. Lo que escribas aquí se lo mandas al cliente.',
        ...TONO.equipo,
    },
    cerrada: {
        nombre: 'Cerrada',
        detalle: 'Nadie contesta. Ni Agendify ni este cuadro.',
        ...TONO.cerrada,
    },
    desconocido: {
        nombre: 'Sin estado',
        detalle: 'No se sabe quién atiende este hilo.',
        punto: 'bg-neutral-600',
        texto: 'text-neutral-400 light:text-neutral-700',
        boton: '',
    },
}

/** El color del botón es el de la marca en la que va a quedar el hilo. */
export function tonoDeAccion(accion: AccionMando): string {
    return TONO[DESTINO[accion]].boton
}

export function marcaDe(mando: Mando | null): MarcaMando {
    if (mando === null) return MARCA_MANDO.desconocido
    return MARCA_MANDO[mando]
}

export const CONFIRMAR_MANDO: Record<AccionMando, { titulo: string; cuerpo: string; boton: string }> = {
    tomar: {
        titulo: 'Contestar tú',
        cuerpo: 'Agendify deja de contestar. El hilo pasa a ti: lo que escribas desde aquí se lo envías al cliente, hasta que se lo devuelvas.',
        boton: 'Contesto yo',
    },
    devolver: {
        titulo: 'Devolver a Agendify',
        cuerpo: 'Dejas de poder escribir. Agendify vuelve a contestar, a partir del siguiente mensaje del cliente. Este clic no le manda nada.',
        boton: 'Devolver a Agendify',
    },
    cerrar: {
        titulo: 'Cerrar el hilo',
        cuerpo: 'El hilo queda cerrado. Agendify no contesta y tú tampoco puedes escribir. Al cliente no se le avisa. Para reabrirlo: Contesto yo, o Devolver a Agendify.',
        boton: 'Cerrar',
    },
}
